import * as Sentry from "@sentry/vue";
import type { App } from "vue";
import type { Router } from "vue-router";
import Cookies from "js-cookie";
import { jwtService } from "@/services/jwt";
import { i18n } from "@/i18n";

// 简单的日志工具
const logger = {
  info: (message: string, ...args: any[]) => {
    console.info(`[Sentry] ${message}`, ...args);
  },
  warn: (message: string, ...args: any[]) => {
    console.warn(`[Sentry] ${message}`, ...args);
  },
  error: (message: string, ...args: any[]) => {
    console.error(`[Sentry] ${message}`, ...args);
  },
};

// 翻译辅助函数
const t = (key: string): string => {
  return i18n.global.t(key);
};

interface SentryConfig {
  enabled: boolean;
  dsn: string;
  environment: string;
  traceSampleRate: number;
  replaySampleRate: number;
}

// 存储设备指纹
let deviceFingerprint: string | null = null;

// 设置设备指纹
export function setDeviceFingerprint(fingerprint: string): void {
  deviceFingerprint = fingerprint;
  // 如果 Sentry 已初始化，更新用户信息
  const user = getUserFromToken();
  if (user) {
    Sentry.setUser(user);
  }
}

/**
 * 从 JWT token 中获取用户信息
 */
function getUserFromToken(): Partial<Sentry.User> | null {
  try {
    const token = jwtService.getToken();
    if (!token) return null;

    const decoded = jwtService.parseToken(token);
    if (!decoded) return null;

    // 根据需求，user.id 对应用户姓名 name，user.username 对应用户学号 username
    return {
      id: decoded.name || decoded.username, // 用户姓名作为主要ID
      username: decoded.username, // 学号作为用户名
      name: decoded.name,
      email: decoded.email,
      ip_address: "{{auto}}",
      device_fingerprint: deviceFingerprint,
      // 添加更多用户信息
      grade: decoded.grade,
      major_class: decoded.major_class,
      phone_number: decoded.phone_number,
      is_staff: decoded.is_staff,
      is_superuser: decoded.is_superuser,
      is_active: decoded.is_active,
    };
  } catch (error) {
    logger.error(t("解析用户令牌失败:"), error);
    return null;
  }
}

/**
 * 获取 Sentry 配置
 */
function getSentryConfig(): SentryConfig {
  return {
    enabled: true,
    dsn: "https://11187ef2ea752049641f2dc93df2fafe@abdnims.scnu.edu.cn/3",
    traceSampleRate: 1.0,
    replaySampleRate: 1.0,
    environment: import.meta.env.VITE_ENV || "production",
  };
}

/**
 * 初始化 Sentry
 */
export function initializeSentry(app: App, router: Router): void {
  const config = getSentryConfig();

  if (!config.enabled || !config.dsn) {
    logger.info(t("Sentry 未启用"));
    return;
  }

  // 初始化 Sentry
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    app,

    // Vue 特定配置
    attachProps: true,
    logErrors: true,
    trackComponents: true,
    hooks: ["activate", "mount", "update"],

    // 集成配置
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.replayIntegration({
        // 由于是内部项目，不需要隐藏信息
        maskAllText: false,
        blockAllMedia: false,
        // 记录所有控制台日志
        maskAllInputs: false,
        // 记录所有网络请求的详细信息
        networkDetailAllowUrls: ["**"],
        networkCaptureBodies: true,
      }),
      // 添加面包屑追踪
      Sentry.browserProfilingIntegration(),
    ],

    // 采样率配置 - 内部项目，尽可能完整记录
    tracesSampleRate: config.traceSampleRate,
    tracePropagationTargets: ["localhost", /^https:\/\/abdnims\.scnu\.edu\.cn/],
    replaysSessionSampleRate: config.replaySampleRate,
    replaysOnErrorSampleRate: 1.0,
    profilesSampleRate: 1.0,

    // 在发送事件前添加用户信息和上下文
    beforeSend(event) {
      try {
        // 添加用户信息
        const user = getUserFromToken();
        if (user) {
          event.user = user;
        }

        // 添加应用版本信息
        event.contexts = {
          ...event.contexts,
          app: {
            version: import.meta.env.VITE_APP_VERSION || "development",
            buildTime: import.meta.env.VITE_APP_BUILD_TIME || "development",
          },
          device: {
            fingerprint: deviceFingerprint,
            userAgent: navigator.userAgent,
            language: navigator.language,
          },
          session: {
            startTime: new Date().toISOString(),
            referrer: document.referrer,
            url: window.location.href,
          },
        };

        return event;
      } catch (error) {
        logger.error(t("处理 Sentry 事件失败:"), error);
        return event;
      }
    },
  });

  // 设置初始用户信息
  const user = getUserFromToken();
  if (user) {
    Sentry.setUser(user);
  }

  // 添加全局标签
  Sentry.setTag(
    "app.version",
    import.meta.env.VITE_APP_VERSION || "development",
  );
  Sentry.setTag("app.environment", config.environment);

  logger.info(t("Sentry 初始化成功"));
}

// 存储当前的 app 和 router 实例
let currentApp: App | undefined;
let currentRouter: Router | undefined;

/**
 * 设置当前的 app 和 router 实例
 */
export function setCurrentInstances(app: App, router: Router): void {
  currentApp = app;
  currentRouter = router;
}

/**
 * 更新 Sentry 配置
 */
export async function updateSentryConfig(): Promise<void> {
  try {
    if (!currentApp || !currentRouter) {
      logger.warn(t("Sentry 实例未初始化"));
      return;
    }

    await Sentry.close();
    initializeSentry(currentApp, currentRouter);
    logger.info(t("Sentry 配置已更新"));
  } catch (error) {
    logger.error(t("更新 Sentry 配置失败:"), error);
  }
}

/**
 * 手动上报错误
 */
export function reportError(error: Error, context?: Record<string, any>): void {
  try {
    // 添加面包屑
    Sentry.addBreadcrumb({
      category: "error",
      message: error.message,
      level: "error",
      data: {
        ...context,
        stack: error.stack,
        name: error.name,
      },
    });

    // 捕获异常
    Sentry.captureException(error, {
      extra: context,
      tags: {
        error_type: context?.type || "unknown",
        error_source: context?.source || "application",
      },
    });
  } catch (e) {
    logger.error(t("上报错误到 Sentry 失败:"), e);
  }
}

/**
 * 手动上报消息
 */
export function reportMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>,
): void {
  try {
    // 添加面包屑
    Sentry.addBreadcrumb({
      category: "message",
      message: message,
      level: level,
      data: context,
    });

    // 捕获消息
    Sentry.captureMessage(message, {
      level,
      extra: context,
    });
  } catch (e) {
    logger.error(t("上报消息到 Sentry 失败:"), e);
  }
}

/**
 * 更新 Sentry 用户信息
 */
export function updateSentryUser(): void {
  try {
    const user = getUserFromToken();
    if (user) {
      Sentry.setUser(user);
    } else {
      Sentry.setUser(null);
    }
  } catch (error) {
    logger.error(t("更新 Sentry 用户信息失败:"), error);
  }
}

/**
 * 清除 Sentry 用户信息
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * 提交用户反馈
 */
export interface UserFeedback {
  name?: string;
  email?: string;
  comments: string;
  eventId?: string;
}

/**
 * 显示错误报告对话框
 */
export function showErrorReportDialog(
  options: {
    eventId?: string;
    title?: string;
    subtitle?: string;
    type?: "error" | "404" | "api" | "runtime";
  } = {},
) {
  const { eventId, type = "error" } = options;

  Sentry.showReportDialog({
    eventId,
    title: options.title || t("发现错误！"),
    subtitle: options.subtitle || t("抱歉给您带来不便"),
    labelName: t("姓名"),
    labelEmail: t("邮箱"),
    labelComments: t("发生了什么？"),
    labelClose: t("关闭"),
    labelSubmit: t("提交反馈"),
    errorGeneric: t("提交反馈时发生未知错误，请稍后再试"),
    errorFormEntry: t("请填写必填字段"),
    successMessage: t("感谢您的反馈！我们已收到您的报告"),
    onLoad: () => {
      // 添加错误类型上下文
      Sentry.setContext("error_report", {
        type,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
    },
  });
}

export async function submitUserFeedback(
  feedback: UserFeedback,
): Promise<void> {
  try {
    const userInfo = getUserFromToken();

    // 创建一个新的事件
    const eventId =
      feedback.eventId ||
      Sentry.captureMessage("User Feedback", {
        level: "info",
        user: {
          username: userInfo?.username,
          email: feedback.email,
        },
      });

    // 使用 Sentry 的反馈 API
    Sentry.captureFeedback({
      name: feedback.name || userInfo?.name || t("匿名用户"),
      email:
        feedback.email ||
        (userInfo?.username ? `${userInfo.username}@m.scnu.edu.cn` : undefined),
      message: feedback.comments,
      associatedEventId: eventId,
    });

    // 添加反馈上下文
    Sentry.setContext("feedback", {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: userInfo?.id,
      deviceFingerprint,
    });

    logger.info(t("用户反馈已提交"), {
      name: feedback.name || userInfo?.name || t("匿名用户"),
      email: feedback.email,
    });
  } catch (error) {
    logger.error(t("提交用户反馈失败:"), error);
    throw error;
  }
}

/**
 * 添加API请求面包屑
 */
export function addRequestBreadcrumb(
  config: any,
  type: "request" | "response" | "error" = "request",
): void {
  try {
    const data: Record<string, any> = {
      url: config.url,
      method: config.method?.toUpperCase() || "GET",
    };

    // 根据类型添加不同的数据
    if (type === "request") {
      // 请求数据，但排除敏感信息
      if (config.data) {
        const safeData = { ...config.data };
        // 移除敏感字段
        const sensitiveFields = [
          "password",
          "old_password",
          "new_password",
          "token",
          "refresh",
          "code",
        ];
        sensitiveFields.forEach((field) => {
          if (field in safeData) {
            safeData[field] = "[REDACTED]";
          }
        });
        data.data = safeData;
      }

      if (config.params) {
        data.params = config.params;
      }

      if (config.headers) {
        // 只保留非敏感的头信息
        const safeHeaders = { ...config.headers };
        if (safeHeaders.Authorization) {
          safeHeaders.Authorization = "Bearer [REDACTED]";
        }
        data.headers = safeHeaders;
      }
    } else if (type === "response") {
      // 响应数据
      if (config.status) {
        data.status = config.status;
        data.statusText = config.statusText;
      }

      // 添加响应数据，但排除大型响应
      if (config.data && typeof config.data === "object") {
        // 创建一个安全的响应数据副本
        const safeResponseData = { ...config.data };
        // 移除可能的敏感信息
        if ("access" in safeResponseData)
          safeResponseData.access = "[REDACTED]";
        if ("refresh" in safeResponseData)
          safeResponseData.refresh = "[REDACTED]";
        if ("token" in safeResponseData) safeResponseData.token = "[REDACTED]";

        data.responseData = safeResponseData;
      }
    } else if (type === "error") {
      // 错误信息
      if (config.status) {
        data.status = config.status;
        data.statusText = config.statusText;
      }

      if (config.data) {
        data.responseData = config.data;
      }

      if (config.message) {
        data.message = config.message;
      }
    }

    // 添加面包屑
    Sentry.addBreadcrumb({
      category: "xhr",
      type: "http",
      level: type === "error" ? "error" : "info",
      data,
      message: `${data.method} ${data.url} ${type === "response" ? data.status || "" : ""}`,
    });
  } catch (e) {
    logger.error(t("添加请求面包屑失败:"), e);
  }
}
